const quizSupport = window.AnatomyQuizSupport;

/* Anchor chip → signal fallback (used only if the bank lacks the anchor node).
   Mirrors the four LOCKED anchor values wired to bridgeTexts in the section. */
const ANCHOR_FALLBACK_SIGNAL = {
  damaged_brittle: { signal: { frustration_theme: 'dryness' }, severity: 2 },
  losing_strength: { signal: { frustration_theme: 'breakage' }, severity: 2 },
  no_elasticity: { signal: { frustration_theme: 'wont_hold' }, severity: 2 },
  prevention: { signal: { frustration_theme: 'prevention' }, severity: 0 }
};

if (!customElements.get('anatomy-quiz')) {
  customElements.define('anatomy-quiz', class AnatomyQuiz extends HTMLElement {

    /* ── Lifecycle ── */

    constructor() {
      super();
      this.currentStep = 'anchor';     // anchor | adaptive | question-loading | email | assessment-loading
      this.conversationHistory = [];    // {question, answer, answer_label, input_type}
      this.questionCount = 0;           // total Qs answered (anchor + adaptive)
      this.sessionId = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2);
      this.sessionToken = '';
      this.sessionExpiresAt = 0;
      this._sessionPromise = null;
      this.abortController = null;
      this.recognition = null;
      this.isRecording = false;
      this._pendingBridgeText = null;   // bridge text from last AI response
      this._assessmentPromise = null;   // pre-fired assessment promise
      this._assessmentResult = null;    // resolved assessment result
      this._assessmentError = null;     // assessment error if failed
      this._assessmentPrefetchPromise = null;
      this._assessmentPrefetchResult = null;
      this._assessmentPrefetchError = null;
      this._assessmentProgress = { value: 0, label: '' };
      this._assessmentTransitionPending = false;
      this._loadingProgressTimer = null;
      this._leadEventId = '';
      this._previewMedia = null;
      this._onPreviewMediaChange = null;

      /* ── Adaptive free-text mode (behind config.adaptiveBank) ── */
      this.adaptiveMarkup = false;      // server rendered chips + composer cluster
      this.useAdaptiveBank = false;     // full bank engine available (chip = instant route)
      this._legacyChipMode = false;     // adaptive markup present but no bank → legacy /api/question rendered as chips
      this.bank = null;                 // window.AnatomyQuizBank
      this.profile = null;              // running structured profile
      this.profileHistory = [];         // [{ profile, nodeId }] snapshots for Back
      this._currentNode = null;         // resolved bank node on screen
      this._currentLegacyQuestion = ''; // last legacy question text (chip fallback)
      this._turnLocked = false;         // re-entrancy guard per turn
      this._lastPct = 0;                // monotonic progress floor
      this._pendingMicroInsight = '';   // micro-insight to paint on the next node
      this._lastPreliminary = null;     // built preliminary object for handoff
      this.qaMode = false;              // explicit QA runs suppress customer/marketing events
    }

    /* Storefront PostHog visitor id (set by anatomy-posthog.js in
       localStorage['anatomy_visitor_id_v1'], mirrored to the anatomy_visitor_id
       cookie). Reusing it as the quiz worker distinct_id ties the server-side
       $ai_generation + quiz-lead events to the same person as the storefront's
       anonymous events. Returns '' unless Customer Privacy authorizes analytics
       and the storefront identity is available. */
    visitorId() {
      if (!quizSupport || !quizSupport.analyticsAllowed || !quizSupport.analyticsAllowed()) return '';
      try {
        const fromStore = window.localStorage.getItem('anatomy_visitor_id_v1');
        if (fromStore) return fromStore;
      } catch (e) {}
      const match = document.cookie.match(/(?:^|; )anatomy_visitor_id=([^;]*)/);
      return match ? decodeURIComponent(match[1]) : '';
    }

    connectedCallback() {
      const configEl = document.getElementById(`QuizConfig-${this.dataset.sectionId}`);
      try {
        this.config = configEl ? JSON.parse(configEl.textContent) : {};
      } catch (e) {
        this.config = {};
      }
      this.strings = this.config.strings || {};
      this.bridgeTexts = this.config.bridgeTexts || {};
      this.fallbackQuestions = this.config.fallbackQuestions || [];
      try { this.qaMode = new URLSearchParams(window.location.search).get('anatomy_qa') === '1'; }
      catch (e) { this.qaMode = false; }

      // Strip trailing slash from worker URL to prevent double-slash in API paths
      if (this.config.workerUrl) {
        this.config.workerUrl = this.config.workerUrl.replace(/\/+$/, '');
      }

      // Cache DOM
      this.progressBar = this.querySelector('.quiz-progress__bar');
      this.progressEl = this.querySelector('.quiz-progress');
      this.phaseEl = this.querySelector('.quiz-phase');
      this.backBtn = this.querySelector('[data-quiz-back]');
      this.kbdHint = this.querySelector('.quiz-kbd-hint');
      this._configurePreviewDisclosure();

      // Bind events
      this.addEventListener('click', this._onClick.bind(this));
      this._keyHandler = this._onKeyDown.bind(this);
      document.addEventListener('keydown', this._keyHandler);

      // Email form
      const emailForm = this.querySelector('[data-email-form]');
      if (emailForm) emailForm.addEventListener('submit', this._onEmailSubmit.bind(this));

      // Transcript submit/cancel
      this.querySelectorAll('[data-transcript-submit]').forEach(btn =>
        btn.addEventListener('click', () => this._submitTranscript()));
      this.querySelectorAll('[data-transcript-cancel]').forEach(btn =>
        btn.addEventListener('click', () => this._cancelTranscript()));

      // Init speech recognition
      this._initSpeechRecognition();

      // ── Adaptive free-text mode bootstrap (additive; flag off = legacy path) ──
      this.adaptiveMarkup = !!this.config.adaptiveBank;
      if (this.adaptiveMarkup) {
        const bank = window.AnatomyQuizBank;
        const bankOk = !!bank
          && bank.NODES
          && typeof bank.selectNext === 'function'
          && typeof bank.mergeSignal === 'function'
          && typeof bank.scoreProfile === 'function';
        if (bankOk) {
          try {
            this.bank = bank;
            // Let the merchant's section sliders drive the bank's finish gate.
            if (typeof bank.configure === 'function') {
              bank.configure({
                minQuestions: this.config.minQuestions,
                maxQuestions: this.config.maxQuestions,
                confidenceTarget: this.config.confidenceTarget
              });
            }
            this.profile = this._initProfile();
            this._currentNode = this._anchorNode();
            this.useAdaptiveBank = true;
          } catch (err) {
            console.error('[Quiz] adaptive bank init failed — degrading to live question flow:', err);
            this.useAdaptiveBank = false;
            this._legacyChipMode = true;
          }
        } else {
          // Markup is present but the bank asset is missing/broken — drive the
          // chips with the live /api/question flow so the quiz never dead-ends.
          this._legacyChipMode = true;
        }

        // Delegated composer events (one set, both anchor + adaptive steps).
        this.addEventListener('submit', (e) => {
          if (e.target.matches('[data-composer]')) {
            e.preventDefault();
            this._onFreeTextSubmit();
          }
        });
        this.addEventListener('input', (e) => {
          if (e.target.matches('[data-freetext]')) this._onComposerInput(e.target);
        });
        this.addEventListener('keydown', (e) => {
          if (e.target.matches('[data-freetext]')) this._onComposerKeydown(e);
        });

        // Hide the desktop letter-key hint — chips have no letter shortcuts.
        if (this.kbdHint) this.kbdHint.style.display = 'none';
      }

      // Restore state if available
      this._restoreState();
      if (this.config.workerUrl) {
        quizSupport.ensureSession(this).catch(err => console.error('[Quiz] session bootstrap failed:', err));
      }

      // Theme editor support
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        this._onSectionSelect = (e) => {
          if (e.detail.sectionId === this.dataset.sectionId) this._showStep('anchor');
        };
        document.addEventListener('shopify:section:select', this._onSectionSelect);
      }

      // Set initial progress
      this._updateProgress();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._keyHandler);
      if (this._onSectionSelect) {
        document.removeEventListener('shopify:section:select', this._onSectionSelect);
      }
      this.abortController?.abort();
      this._assessmentAbort?.abort();
      if (this._loadingProgressTimer) clearInterval(this._loadingProgressTimer);
      if (this._previewMedia && this._onPreviewMediaChange) {
        if (typeof this._previewMedia.removeEventListener === 'function') {
          this._previewMedia.removeEventListener('change', this._onPreviewMediaChange);
        } else if (typeof this._previewMedia.removeListener === 'function') {
          this._previewMedia.removeListener(this._onPreviewMediaChange);
        }
      }
      this._stopRecording();
    }

    /* ── Event Handlers ── */

    _configurePreviewDisclosure() {
      const details = this.querySelector('[data-preview-details]');
      if (!details || typeof window.matchMedia !== 'function') return;

      this._previewMedia = window.matchMedia('(max-width: 768px)');
      this._onPreviewMediaChange = (event) => {
        // Native disclosure keeps the full preview keyboard-accessible while
        // leaving the consent copy and primary CTA in the mobile viewport.
        details.open = !event.matches;
      };
      this._onPreviewMediaChange(this._previewMedia);
      if (typeof this._previewMedia.addEventListener === 'function') {
        this._previewMedia.addEventListener('change', this._onPreviewMediaChange);
      } else if (typeof this._previewMedia.addListener === 'function') {
        this._previewMedia.addListener(this._onPreviewMediaChange);
      }
    }

    _onClick(e) {
      if (this.adaptiveMarkup) {
        const chip = e.target.closest('.quiz-chip');
        if (chip) {
          this._onChip(chip);
          return;
        }
      }

      const option = e.target.closest('.quiz-option');
      if (option) {
        this._selectOption(option);
        return;
      }

      const micBtn = e.target.closest('[data-mic]');
      if (micBtn) {
        this._toggleRecording(micBtn);
        return;
      }

      const backBtn = e.target.closest('[data-quiz-back]');
      if (backBtn) {
        this._goBack();
        return;
      }

      const emailSkip = e.target.closest('[data-email-skip]');
      if (emailSkip) {
        this._continueWithoutEmail();
      }

    }

    _onKeyDown(e) {
      if (this.adaptiveMarkup) return;   // chips have no a–f letter shortcuts
      if (this.currentStep !== 'anchor' && this.currentStep !== 'adaptive') return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      const keyMap = { a: 0, b: 1, c: 2, d: 3, e: 4, f: 5 };
      const idx = keyMap[e.key.toLowerCase()];
      if (idx !== undefined) {
        const activeStep = this.querySelector(`.quiz-step:not([hidden]) .quiz-options`);
        const options = activeStep?.querySelectorAll('.quiz-option');
        if (options && options[idx]) {
          e.preventDefault();
          this._selectOption(options[idx]);
        }
      }
    }

    /* ── Option Selection ── */

    _selectOption(option) {
      const step = option.closest('.quiz-step');
      const allOptions = step.querySelectorAll('.quiz-option');

      // Visual: mark selected
      allOptions.forEach(o => {
        o.classList.remove('quiz-option--selected');
        o.setAttribute('aria-checked', 'false');
      });
      option.classList.add('quiz-option--selected');
      option.setAttribute('aria-checked', 'true');

      const value = option.dataset.value;
      const label = option.querySelector('.quiz-option__text')?.textContent?.trim() || value;

      // Record answer
      if (this.currentStep === 'anchor') {
        this.conversationHistory = [{
          question: this.strings.anchorQuestion || '',
          answer: value,
          answer_label: label,
          input_type: 'option'
        }];
        this.questionCount = 1;

        this._track('QuizStarted', { content_name: 'Hair Consultation', primary_concern: value });
        this._track('QuizProgress', { content_name: 'Hair Consultation', question_number: 1, answer: label });

        // Set bridge text from anchor answer before the loading screen
        this._pendingBridgeText = this._getDefaultBridgeText();

        // Fire the network request immediately — don't wait for animation
        this._prefetchedQuestion = this._startQuestionFetch();
        // Show transition after a short delay for selection feedback
        setTimeout(() => this._advanceFromPrefetch(), 150);
      } else if (this.currentStep === 'adaptive') {
        const questionText = this.querySelector('[data-adaptive-question]')?.textContent || '';
        this.conversationHistory.push({
          question: questionText,
          answer: value,
          answer_label: label,
          input_type: 'option'
        });
        this.questionCount++;

        this._track('QuizProgress', { content_name: 'Hair Consultation', question_number: this.questionCount, answer: label });

        // Check if complete flag was set on the last response OR max questions reached
        if (this._lastQuestionComplete || this.questionCount >= 6) {
          this._fireAssessmentEarly();
          setTimeout(() => this._showAssessmentPreview(), 150);
        } else {
          // Fire the network request immediately — don't wait for animation
          this._prefetchedQuestion = this._startQuestionFetch();
          setTimeout(() => this._advanceFromPrefetch(), 150);
        }
      }
    }

    /* ── Voice / Freeform Input ── */

    _initSpeechRecognition() {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        if (this.adaptiveMarkup) {
          // Composer is already a text field — no mic needed.
          this.querySelectorAll('[data-mic]').forEach(btn => { btn.hidden = true; });
          return;
        }
        // Fallback: show text input instead of mic
        this.querySelectorAll('.quiz-voice-label').forEach(l => {
          l.textContent = this.strings.voiceFallback || '';
        });
        this.querySelectorAll('[data-mic]').forEach(btn => {
          btn.addEventListener('click', () => this._showTranscriptArea(btn));
        });
        return;
      }

      this.recognition = new SpeechRecognition();
      this.recognition.continuous = false;
      this.recognition.interimResults = true;
      this.recognition.lang = 'en-US';

      this.recognition.onresult = (event) => {
        let transcript = '';
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        const textArea = this._activeFreeTextEl();
        if (textArea) {
          textArea.value = transcript;
          if (this.adaptiveMarkup) this._onComposerInput(textArea);
        }
      };

      this.recognition.onend = () => {
        this.isRecording = false;
        this.querySelectorAll('[data-mic]').forEach(btn =>
          btn.classList.remove('quiz-mic-btn--recording'));
      };

      this.recognition.onerror = (event) => {
        this.isRecording = false;
        this.querySelectorAll('[data-mic]').forEach(btn =>
          btn.classList.remove('quiz-mic-btn--recording'));
        // On error, fall back to text input
        const micBtn = this.querySelector(`.quiz-step:not([hidden]) [data-mic]`);
        if (micBtn) this._showTranscriptArea(micBtn);
      };
    }

    _toggleRecording(micBtn) {
      if (this.isRecording) {
        this._stopRecording();
        return;
      }

      if (this.adaptiveMarkup) {
        // No separate transcript area — record straight into the composer.
        if (!this.recognition) {
          const ta = this._activeFreeTextEl();
          if (ta) ta.focus();
          return;
        }
        try {
          this.isRecording = true;
          micBtn.classList.add('quiz-mic-btn--recording');
          this.recognition.start();
        } catch (e) {
          this.isRecording = false;
          micBtn.classList.remove('quiz-mic-btn--recording');
        }
        return;
      }

      // Show transcript area
      this._showTranscriptArea(micBtn);

      if (this.recognition) {
        try {
          this.isRecording = true;
          micBtn.classList.add('quiz-mic-btn--recording');
          this.recognition.start();
        } catch (e) {
          this.isRecording = false;
          micBtn.classList.remove('quiz-mic-btn--recording');
        }
      }
    }

    _stopRecording() {
      if (this.recognition && this.isRecording) {
        this.recognition.stop();
      }
      this.isRecording = false;
      this.querySelectorAll('[data-mic]').forEach(btn =>
        btn.classList.remove('quiz-mic-btn--recording'));
    }

    _showTranscriptArea(micBtn) {
      const step = micBtn.closest('.quiz-step');
      const transcriptArea = step.querySelector('.quiz-transcript');
      if (transcriptArea) {
        transcriptArea.hidden = false;
        const textArea = transcriptArea.querySelector('[data-transcript]');
        if (textArea) {
          textArea.focus();
          // Scroll submit button into view on mobile when keyboard opens
          setTimeout(() => {
            const actions = transcriptArea.querySelector('.quiz-transcript__actions');
            if (actions) actions.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }, 350);
        }
      }
    }

    _getActiveTranscript() {
      return this.querySelector(`.quiz-step:not([hidden]) [data-transcript]`);
    }

    _submitTranscript() {
      this._stopRecording();
      const textArea = this._getActiveTranscript();
      if (!textArea || !textArea.value.trim()) return;

      const freeformText = textArea.value.trim();
      const questionText = this.currentStep === 'anchor'
        ? (this.strings.anchorQuestion || '')
        : (this.querySelector('[data-adaptive-question]')?.textContent || '');

      if (this.currentStep === 'anchor') {
        this.conversationHistory = [{
          question: questionText,
          answer: 'freeform',
          answer_label: freeformText,
          input_type: this.isRecording ? 'voice' : 'text'
        }];
        this.questionCount = 1;
        this._track('QuizStarted', { content_name: 'Hair Consultation', primary_concern: 'freeform' });
        this._track('QuizProgress', { content_name: 'Hair Consultation', question_number: 1, answer: freeformText, input_type: 'freeform' });
      } else {
        this.conversationHistory.push({
          question: questionText,
          answer: 'freeform',
          answer_label: freeformText,
          input_type: this.isRecording ? 'voice' : 'text'
        });
        this.questionCount++;
        this._track('QuizProgress', { content_name: 'Hair Consultation', question_number: this.questionCount, answer: freeformText, input_type: 'freeform' });
      }

      textArea.value = '';
      textArea.closest('.quiz-transcript').hidden = true;

      if (this._lastQuestionComplete || this.questionCount >= 6) {
        this._fireAssessmentEarly();
        this._showAssessmentPreview();
      } else {
        this._fetchNextQuestion();
      }
    }

    _cancelTranscript() {
      this._stopRecording();
      const step = this.querySelector(`.quiz-step:not([hidden])`);
      const transcriptArea = step?.querySelector('.quiz-transcript');
      if (transcriptArea) {
        transcriptArea.hidden = true;
        const textArea = transcriptArea.querySelector('[data-transcript]');
        if (textArea) textArea.value = '';
      }
    }

    /* ── Step Navigation ── */

    _showStep(name, direction = 'forward') {
      const nextEl = this.querySelector(`[data-step="${name}"]`);
      if (!nextEl) return;

      const exitClass = direction === 'forward' ? 'quiz-step--exit-left' : 'quiz-step--exit-right';
      const enterClass = direction === 'forward' ? 'quiz-step--enter-right' : 'quiz-step--enter-left';

      // Hide ALL other visible steps (fixes race condition when API responds fast)
      this.querySelectorAll('.quiz-step:not([hidden])').forEach(step => {
        if (step === nextEl) return;
        step.classList.add(exitClass);
        setTimeout(() => {
          step.hidden = true;
          step.classList.remove(exitClass, 'quiz-step--exit-left', 'quiz-step--exit-right');
        }, 300);
      });

      nextEl.hidden = false;
      nextEl.classList.add(enterClass);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          nextEl.classList.remove(enterClass);
        });
      });

      this.currentStep = name;
      this._updateProgress();

      // Back button visibility
      if (this.backBtn) {
        this.backBtn.hidden = (name === 'anchor' || name === 'assessment-loading');
      }

      // Keyboard hint
      if (this.kbdHint) {
        this.kbdHint.style.display = (!this.adaptiveMarkup && (name === 'anchor' || name === 'adaptive')) ? '' : 'none';
      }

    }

    _goBack() {
      if (this.adaptiveMarkup) {
        return this._goBackAdaptive();
      }

      if (this.currentStep === 'email') {
        // Go back to last adaptive Q (re-render it)
        if (this._lastAdaptiveResponse) {
          this._renderAdaptiveQuestion(this._lastAdaptiveResponse);
          this._showStep('adaptive', 'back');
        }
        return;
      }

      if (this.currentStep === 'adaptive') {
        // Remove last conversation entry
        if (this.conversationHistory.length > 1) {
          this.conversationHistory.pop();
          this.questionCount--;
        }

        if (this.conversationHistory.length <= 1) {
          // Back to anchor
          this._showStep('anchor', 'back');
          // Restore anchor selection
          const anchorAnswer = this.conversationHistory[0]?.answer;
          if (anchorAnswer) {
            const anchorStep = this.querySelector('[data-step="anchor"]');
            anchorStep?.querySelectorAll('.quiz-option').forEach(o => {
              const selected = o.dataset.value === anchorAnswer;
              o.classList.toggle('quiz-option--selected', selected);
              o.setAttribute('aria-checked', selected ? 'true' : 'false');
            });
          }
        } else {
          // Re-fetch previous question state (or show anchor if only 1 answer)
          this._fetchNextQuestion('back');
        }
      }
    }

    /* ── Progress ── */

    _updateProgress() {
      if (this.useAdaptiveBank && (this.currentStep === 'anchor' || this.currentStep === 'adaptive' || this.currentStep === 'question-loading')) {
        return this._updateProgressAdaptive();
      }

      const phases = {
        anchor: { label: this.strings.phaseUnderstanding || '', pct: 10 },
        adaptive: { label: this._getPhaseLabel(), pct: Math.min(15 + this.questionCount * 14, 85) },
        'question-loading': { label: this._getPhaseLabel(), pct: Math.min(15 + this.questionCount * 14, 85) },
        email: { label: this.strings.emailReady || '', pct: 90 },
        'assessment-loading': { label: this.strings.assessmentLoading || '', pct: 95 }
      };

      const phase = phases[this.currentStep] || phases.anchor;
      this.progressBar.style.setProperty('--progress', `${phase.pct}%`);
      this.progressEl.setAttribute('aria-valuenow', Math.round(phase.pct / 100 * 7));
      if (this.phaseEl) {
        this.phaseEl.textContent = phase.label;
        this.phaseEl.style.opacity = phase.label ? '1' : '0';
      }
    }

    _getPhaseLabel() {
      if (this.questionCount <= 2) return this.strings.phaseUnderstanding || '';
      if (this.questionCount <= 4) return this.strings.phaseMapping || '';
      return this.strings.phaseRefining || '';
    }

    /* ── API: Fetch Next Question ── */

    /**
     * Start the question fetch immediately (before animation completes).
     * Returns a promise that resolves to the API response data.
     */
    _startQuestionFetch() {
      if (!this.config.workerUrl) return Promise.resolve(null);

      this.abortController?.abort();
      this.abortController = new AbortController();
      const timeout = setTimeout(() => this.abortController.abort(), 25000);

      const latestAnswer = this.conversationHistory[this.conversationHistory.length - 1] || null;
      return quizSupport.workerFetch(this, '/api/question', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_history: this.conversationHistory,
          session_id: this.sessionId,
          anatomy_visitor_id: this.visitorId(),
          customer_privacy: quizSupport.customerPrivacySnapshot(),
          answer: latestAnswer
        }),
        signal: this.abortController.signal
      })
        .then(r => { clearTimeout(timeout); if (!r.ok) throw new Error(`API ${r.status}`); return r.json(); })
        .catch(err => { clearTimeout(timeout); throw err; });
    }

    /**
     * Show the loading transition, then await the already-in-flight prefetch.
     */
    async _advanceFromPrefetch(direction = 'forward') {
      this._showBridgeText();
      this._showStep('question-loading', direction);

      try {
        const data = await (this._prefetchedQuestion || this._startQuestionFetch());
        this._prefetchedQuestion = null;

        if (!data) {
          this._renderFallbackQuestion();
          this._showStep('adaptive', direction);
          return;
        }

        this._lastAdaptiveResponse = data;
        this._lastQuestionComplete = data.complete || false;

        const loadingStep = this.querySelector('[data-step="question-loading"]');
        if (loadingStep) {
          loadingStep.hidden = true;
          loadingStep.classList.remove('quiz-step--exit-left', 'quiz-step--exit-right');
        }

        if (data.complete) {
          this._fireAssessmentEarly();
          this._showAssessmentPreview(direction);
        } else {
          this._renderAdaptiveQuestion(data);
          this._showStep('adaptive', direction);
        }
      } catch (err) {
        this._prefetchedQuestion = null;
        if (err.name === 'AbortError') return;
        console.error('Quiz question fetch failed:', err);
        if (this.questionCount >= 3) {
          this._fireAssessmentEarly();
          this._showAssessmentPreview(direction);
        } else {
          this._renderFallbackQuestion();
          this._showStep('adaptive', direction);
        }
      }
    }

    async _fetchNextQuestion(direction = 'forward') {
      this._prefetchedQuestion = null;
      return this._advanceFromPrefetch(direction);
    }

    _renderAdaptiveQuestion(data) {
      // Store bridge text for the next loading screen (with answer-aware fallback)
      // Reject generic/placeholder bridge text from the model
      const bridge = data.bridge_text || '';
      const isGeneric = !bridge || /\b(loading|please wait|one moment|hang tight|stand by)\b/i.test(bridge) || /\bnext (question|one)\b/i.test(bridge);
      this._pendingBridgeText = isGeneric ? this._getDefaultBridgeText() : bridge;

      const insightEl = this.querySelector('[data-adaptive-insight]');
      const contextEl = this.querySelector('[data-adaptive-context]');
      const questionEl = this.querySelector('[data-adaptive-question]');
      const optionsEl = this.querySelector('[data-adaptive-options]');

      if (insightEl) {
        insightEl.textContent = data.micro_insight || '';
        insightEl.hidden = !data.micro_insight;
      }
      if (contextEl) {
        contextEl.textContent = data.question_context || '';
        contextEl.hidden = !data.question_context;
      }
      if (questionEl) questionEl.textContent = data.question_text || '';
      if (optionsEl) {
        const letters = ['A', 'B', 'C', 'D', 'E', 'F'];
        optionsEl.innerHTML = (data.options || []).map((opt, i) =>
          `<button class="quiz-option" type="button" data-value="${this._escapeAttr(opt.value)}" role="radio" aria-checked="false">
            <span class="quiz-option__key">${letters[i] || ''}</span>
            <span class="quiz-option__text">${this._escapeHtml(opt.label)}</span>
          </button>`
        ).join('');

        // Update kbd hint
        if (this.kbdHint) {
          const keys = letters.slice(0, (data.options || []).length).join(', ');
          this.kbdHint.textContent = (this.strings.keyboardHintTemplate || '').replace('{keys}', keys);
        }
      }

      // Reset transcript
      const transcript = this.querySelector('[data-step="adaptive"] .quiz-transcript');
      if (transcript) {
        transcript.hidden = true;
        const ta = transcript.querySelector('[data-transcript]');
        if (ta) ta.value = '';
      }

      // Debug panel
      this._renderDebug(data);
    }

    _renderFallbackQuestion() {
      const fallbacks = this.fallbackQuestions;

      const used = this.conversationHistory.map(h => h.question);
      const fallback = fallbacks.find(f => !used.includes(f.question_text)) || fallbacks[0];
      this._lastQuestionComplete = true; // After fallback, go to email
      this._lastAdaptiveResponse = fallback;
      this._renderAdaptiveQuestion(fallback);
    }

    /* ── Bridge Text (between questions) ── */

    _showBridgeText() {
      const bridgeEl = this.querySelector('[data-bridge-text]');
      if (!bridgeEl) return;

      if (this._pendingBridgeText) {
        bridgeEl.textContent = this._pendingBridgeText;
        bridgeEl.hidden = false;
      } else {
        bridgeEl.textContent = '';
        bridgeEl.hidden = true;
      }
    }

    _getDefaultBridgeText() {
      const lastAnswer = this.conversationHistory[this.conversationHistory.length - 1];
      if (!lastAnswer) return null;

      if (this.questionCount === 1) {
        const bridges = {
          damaged_brittle: this.bridgeTexts.damaged_brittle,
          losing_strength: this.bridgeTexts.losing_strength,
          no_elasticity: this.bridgeTexts.no_elasticity,
          prevention: this.bridgeTexts.prevention
        };
        return bridges[lastAnswer.answer] || this.bridgeTexts.default || null;
      }

      if (this.questionCount === 2) return this.bridgeTexts.step_2 || null;
      if (this.questionCount === 3) return this.bridgeTexts.step_3 || null;
      if (this.questionCount >= 4) return this.bridgeTexts.step_4 || null;

      return null;
    }

    /* ── Early Assessment (fire before email) ── */

    _fireAssessmentEarly() {
      if (this._assessmentPromise || this._assessmentResult) return;
      this._assessmentResult = null;
      this._assessmentError = null;

      this._assessmentPromise = this._fetchAssessmentAsync()
        .then(result => {
          this._assessmentResult = result;
          return result;
        })
        .catch(err => {
          this._assessmentError = err;
          throw err;
        });
    }

    async _fetchAssessmentAsync() {
      this._assessmentAbort?.abort();
      this._assessmentAbort = new AbortController();

      const response = await quizSupport.workerFetch(this, '/api/assessment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: this.sessionId,
          anatomy_visitor_id: this.visitorId(),
          customer_privacy: quizSupport.customerPrivacySnapshot(),
          conversation_history: this.conversationHistory,
          profile: this.profile || null
        }),
        signal: this._assessmentAbort.signal
      });

      if (!response.ok) throw new Error(`API ${response.status}`);
      return response.json();
    }

    _startAssessmentPrefetch() {
      if (!this.useAdaptiveBank || this._legacyChipMode || !this.config.workerUrl) return null;
      if (this._assessmentPrefetchPromise || this._assessmentPrefetchResult) {
        return this._assessmentPrefetchPromise;
      }

      this._assessmentPrefetchError = null;
      this._setAssessmentProgress(0.04, 'Reading your answers');
      const canStream = !!this.config.streamAssessment
        && typeof window.ReadableStream !== 'undefined'
        && typeof fetch === 'function';
      const request = canStream ? this._fetchAssessmentStreamEarly() : this._fetchAssessmentAsync();

      this._assessmentPrefetchPromise = request
        .then((result) => {
          if (!this._isCompleteAssessmentProfile(result)) throw new Error('Assessment response was incomplete');
          this._assessmentPrefetchResult = result;
          this._setAssessmentProgress(0.96, 'Preparing your results');
          return result;
        })
        .catch((error) => {
          this._assessmentPrefetchError = error;
          console.warn('[Quiz] assessment prefetch unavailable; results page fallback remains active:', error);
          return null;
        });

      return this._assessmentPrefetchPromise;
    }

    async _fetchAssessmentStreamEarly() {
      const response = await quizSupport.workerFetch(this, '/api/assessment-stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
        body: JSON.stringify({
          session_id: this.sessionId,
          anatomy_visitor_id: this.visitorId(),
          customer_privacy: quizSupport.customerPrivacySnapshot(),
          conversation_history: this.conversationHistory,
          profile: this.profile || null,
          products: this.products || []
        })
      });

      const contentType = response.headers.get('Content-Type') || '';
      if (response.status === 202) throw new Error('Assessment is already running');
      if (!response.ok || contentType.indexOf('text/event-stream') === -1) {
        throw new Error(`Assessment stream ${response.status}`);
      }

      let completedProfile = null;
      let streamError = null;
      await quizSupport.consumeEventStream(response, (eventName, dataText) => {
        let data = {};
        if (dataText) {
          try { data = JSON.parse(dataText); }
          catch (error) { return; }
        }

        if (eventName === 'status') {
          if (data.phase === 'thinking') this._setAssessmentProgress(0.08, 'Analyzing damage vectors');
          if (data.phase === 'writing') this._setAssessmentProgress(0.16, 'Mapping your damage profile');
        } else if (eventName === 'progress') {
          const upstreamProgress = Math.max(0, Math.min(1, Number(data.value) || 0));
          this._setAssessmentProgress(0.16 + (upstreamProgress * 0.8), data.label || 'Building your profile');
        } else if (eventName === 'complete') {
          completedProfile = data.profile || null;
          this._setAssessmentProgress(0.96, 'Preparing your results');
        } else if (eventName === 'error') {
          streamError = new Error(data.message || 'Assessment stream failed');
        }
      });

      if (streamError) throw streamError;
      if (!completedProfile) throw new Error('Assessment stream ended before completion');
      return completedProfile;
    }

    _isCompleteAssessmentProfile(profile) {
      const hasCore = !!(
        profile
        && typeof profile === 'object'
        && profile.damage_profile_headline
        && profile.damage_profile_summary
        && profile.what_is_happening
      );
      if (!hasCore) return false;
      if (profile.in_scope === false) return true;
      return !!(profile.how_anatomy_works_for_you && profile.reconstruction_protocol);
    }

    _setAssessmentProgress(value, label) {
      const nextValue = Math.max(
        this._assessmentProgress.value || 0,
        Math.max(0, Math.min(1, Number(value) || 0))
      );
      const nextLabel = label || this._assessmentProgress.label || 'Analyzing damage vectors';
      this._assessmentProgress = { value: nextValue, label: nextLabel };

      const percent = Math.round(nextValue * 100);
      const progress = this.querySelector('[data-assessment-progress]');
      const bar = this.querySelector('[data-assessment-progress-bar]');
      const status = this.querySelector('[data-assessment-status]');
      const output = this.querySelector('[data-assessment-percent]');
      if (bar) bar.style.width = `${percent}%`;
      if (status) status.textContent = nextLabel;
      if (output) output.textContent = `${percent}%`;
      if (progress) {
        progress.setAttribute('aria-valuenow', String(percent));
        progress.setAttribute('aria-valuetext', `${percent}%: ${nextLabel}`);
      }

      const activeIndex = percent >= 92 ? 3 : percent >= 62 ? 2 : percent >= 28 ? 1 : 0;
      this.querySelectorAll('[data-loading-step]').forEach((step, index) => {
        step.classList.toggle('quiz-loading__step--done', percent === 100 || index < activeIndex);
        step.classList.toggle('quiz-loading__step--active', percent < 100 && index === activeIndex);
      });
    }

    /* ── Debug Panel ── */

    async _ensureAssessmentResult() {
      if (this._assessmentResult) return this._assessmentResult;

      if (this._assessmentPromise) {
        await this._assessmentPromise;

        if (this._assessmentResult) return this._assessmentResult;
        if (this._assessmentError) throw this._assessmentError;
      }

      const result = await this._fetchAssessmentAsync();
      this._assessmentResult = result;
      return result;
    }

    async _showAssessmentPreview(direction = 'forward') {
      // First-class quiz_completed for the legacy/chip flows (the adaptive-bank
      // flow fires it in _finishAdaptive). prelim is best-effort here.
      if (direction === 'forward' && !this._completedFired) {
        this._completedFired = true;
        const prelim = this._lastPreliminary || this._assessmentResult || null;
        this._track('QuizCompleted', {
          total_questions: (this.profile && this.profile.asked_ids ? this.profile.asked_ids.length : this.questionCount) || 0,
          primary_concern: (this.conversationHistory[0] && this.conversationHistory[0].answer) || '',
          severity: (prelim && prelim.damage_severity) || (this.profile && this.profile.severity) || '',
          dominant_mechanism: (this.profile && this.profile.dominant_mechanism) || '',
          confidence: (this.profile && this.profile.confidence) || 0,
          entry_point: (prelim && prelim.reconstruction_protocol && prelim.reconstruction_protocol.entry_point) || '',
          completion_path: this._legacyChipMode ? 'legacy' : 'adaptive',
          had_freetext: (this.conversationHistory || []).some(t => t && t.input_type === 'freeform')
        });
      }
      if (direction === 'forward') {
        this._showStep('assessment-loading');
      } else {
        this._showStep('assessment-loading', direction);
      }
      this._animateLoadingSteps();

      try {
        const result = await this._ensureAssessmentResult();
        this._renderPreview(result);

        if (direction === 'forward') {
          this._showStep('email');
        } else {
          this._showStep('email', direction);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Quiz assessment preview failed:', err);

        const fallback = this._getFallbackResult();
        this._assessmentResult = fallback;
        this._assessmentError = null;
        this._assessmentPromise = Promise.resolve(fallback);
        this._renderPreview(fallback);

        if (direction === 'forward') {
          this._showStep('email');
        } else {
          this._showStep('email', direction);
        }
      }
    }

    _renderPreview(result) {
      const fallback = this._getFallbackResult();
      const preview = {
        ...fallback,
        ...(result || {}),
        reconstruction_protocol: {
          ...(fallback.reconstruction_protocol || {}),
          ...((result && result.reconstruction_protocol) || {}),
        },
      };

      const severityEl = this.querySelector('[data-preview-severity]');
      const timelineEl = this.querySelector('[data-preview-timeline]');
      const headlineEl = this.querySelector('[data-preview-headline]');
      const summaryEl = this.querySelector('[data-preview-summary]');
      const insightEl = this.querySelector('[data-preview-insight]');
      const entryPointEl = this.querySelector('[data-preview-entry-point]');

      if (severityEl) {
        severityEl.textContent = this._formatSeverity(preview.damage_severity);
      }

      if (timelineEl) {
        const timeline = preview.reconstruction_protocol?.expected_timeline || '';
        timelineEl.textContent = timeline;
        timelineEl.hidden = !timeline;
      }

      if (headlineEl) {
        headlineEl.textContent = preview.damage_profile_headline || fallback.damage_profile_headline;
      }

      if (summaryEl) {
        summaryEl.textContent = preview.damage_profile_summary || fallback.damage_profile_summary;
      }

      if (insightEl) {
        insightEl.textContent =
          preview.key_insight ||
          preview.what_is_happening ||
          preview.how_anatomy_works_for_you ||
          fallback.how_anatomy_works_for_you;
      }

      if (entryPointEl) {
        const entryPoint =
          this._formatProtocolEntry(preview.reconstruction_protocol?.entry_point) ||
          preview.reconstruction_protocol?.intensive_phase ||
          fallback.reconstruction_protocol?.intensive_phase ||
          '';
        entryPointEl.textContent = entryPoint;
      }
    }

    _clearPreview() {
      ['severity', 'timeline', 'headline', 'summary', 'insight', 'entry-point'].forEach((key) => {
        const el = this.querySelector(`[data-preview-${key}]`);
        if (!el) return;
        el.textContent = '';
        if (key === 'timeline') el.hidden = true;
      });
    }

    _formatSeverity(value) {
      if (!value) return '';
      return value
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }

    _formatProtocolEntry(value) {
      if (!value) return '';
      return value
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }

    _renderDebug(data) {
      quizSupport.renderDebug(this, data);
    }

    async _onEmailSubmit(e) {
      e.preventDefault();
      if (this._assessmentTransitionPending) return;
      const emailInput = this.querySelector('.quiz-email-input');
      emailInput.setCustomValidity('');
      if (!emailInput.checkValidity()) {
        emailInput.reportValidity();
        return;
      }

      const email = emailInput.value.trim().toLowerCase();
      this._leadEventId = this._leadEventId || (crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      this._assessmentTransitionPending = true;
      this._setEmailSubmissionState('pending');

      try {
        if (this.useAdaptiveBank && !this._legacyChipMode) {
          await this._completeAdaptiveHandoff(email, this._leadEventId);
          return;
        }

        const result = await this._ensureAssessmentResult();
        await this._sendEmail(email, this._leadEventId);
        this._commitSuccessfulEmail(email, this._leadEventId);
        this._setEmailSubmissionState('success', 'Saved. Opening your results...');
        this._showStep('assessment-loading');
        this._animateLoadingSteps();
        this._storeAndRedirect(result, email);
      } catch (err) {
        if (err.name === 'AbortError') {
          this._assessmentTransitionPending = false;
          return;
        }
        console.error('Quiz completion failed:', err);
        this._assessmentTransitionPending = false;
        this._showEmailCompletionError(emailInput, err.message);
      }
    }

    _showEmailCompletionError(emailInput, message) {
      this._showStep('email');
      const safeMessage = String(message || this.strings.emailError || 'We could not save and email your consultation yet. Your answers are still here; please try again.').slice(0, 320);
      this._setEmailSubmissionState('retryable', safeMessage);
      if (emailInput) {
        emailInput.setCustomValidity('');
        emailInput.setAttribute('aria-invalid', 'true');
        emailInput.focus();
      }
    }

    _setEmailSubmissionState(state, message = '') {
      const form = this.querySelector('[data-email-form]');
      if (!form) return;
      const input = form.querySelector('.quiz-email-input');
      const submit = form.querySelector('.quiz-submit-btn');
      const skip = form.querySelector('[data-email-skip]');
      const status = form.querySelector('[data-email-status]');
      const error = form.querySelector('[data-email-error]');
      const pending = state === 'pending';
      const retryable = state === 'retryable';

      form.dataset.emailState = state;
      form.setAttribute('aria-busy', pending ? 'true' : 'false');
      if (input) {
        input.readOnly = pending || state === 'success';
        if (!retryable) input.removeAttribute('aria-invalid');
      }
      if (submit) {
        submit.disabled = pending || state === 'success';
        submit.textContent = retryable
          ? (submit.dataset.retryLabel || this.strings.emailRetry || 'Try again')
          : (submit.dataset.defaultLabel || submit.textContent);
      }
      if (skip) skip.disabled = pending || state === 'success';
      if (status) {
        status.hidden = !(pending || state === 'success');
        status.textContent = message || (pending
          ? (this.strings.emailPending || 'Saving your consultation securely...')
          : 'Saved. Opening your results...');
      }
      if (error) {
        error.hidden = !retryable;
        if (retryable) error.textContent = message;
      }
    }

    async _continueWithoutEmail() {
      if (this._assessmentTransitionPending) return;
      this._assessmentTransitionPending = true;
      try {
        if (this.useAdaptiveBank && !this._legacyChipMode) {
          await this._completeAdaptiveHandoff('');
          return;
        }
        this._showStep('assessment-loading');
        this._animateLoadingSteps();
        const result = await this._ensureAssessmentResult();
        this._storeAndRedirect(result, '');
      } catch (err) {
        if (err.name === 'AbortError') {
          this._assessmentTransitionPending = false;
          return;
        }
        console.error('Quiz assessment failed:', err);
        this._assessmentTransitionPending = false;
        this._showEmailCompletionError(
          this.querySelector('.quiz-email-input'),
          'We could not complete your assessment yet. Please try again.'
        );
      }
    }

    async _sendEmail(email, leadEventId) {
      if (this.qaMode) return { success: true, qa_suppressed: true };
      if (!this.config.workerUrl) {
        throw new Error('The consultation email service is unavailable. Please try again.');
      }

      const firstTurn = (this.conversationHistory && this.conversationHistory[0]) || {};
      const primaryConcern = String(
        firstTurn.input_type === 'freeform'
          ? 'freeform'
          : (firstTurn.answer || firstTurn.answer_label || 'unspecified')
      )
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 160) || 'unspecified';
      const getCookie = (name) => {
        const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
        return match ? decodeURIComponent(match[1]) : '';
      };
      const customerPrivacy = quizSupport.customerPrivacySnapshot();
      const emailPayload = {
        contract_version: 2,
        email,
        session_id: this.sessionId,
        anatomy_visitor_id: quizSupport.analyticsAllowed(customerPrivacy) ? this.visitorId() : '',
        primary_concern: primaryConcern,
        total_questions: Math.max(1, Math.min(6, Number(this.questionCount) || (this.conversationHistory || []).length || 1)),
        subscribe_marketing: true,
        marketing_consent_version: quizSupport.marketingConsentVersion,
        wait_for_completion: true,
        capture_context: 'email_gate',
        customer_privacy: customerPrivacy,
      };
      if (quizSupport.advertisingAllowed(customerPrivacy)) {
        emailPayload.fb = {
          event_id: leadEventId,
          event_source_url: window.location.href,
          fbp: getCookie('_fbp'),
          fbc: getCookie('_fbc')
        };
      }

      const response = await quizSupport.workerFetch(this, '/api/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify(emailPayload)
      });
      const data = await response.json().catch(() => ({}));
      const state = String(data.status || data.completion_status || '').toLowerCase();
      const deferred = response.status === 202
        || data.deferred === true
        || ['accepted', 'deferred', 'pending', 'queued', 'running'].includes(state);
      if (!response.ok || deferred || data.success === false) {
        const fallback = deferred
          ? 'Your consultation is still being prepared. Please try again in a moment.'
          : `Email API ${response.status}`;
        const error = new Error(String(data.error || fallback).slice(0, 320));
        error.status = response.status;
        error.retryable = true;
        error.deferred = deferred;
        throw error;
      }
      return data;
    }

    _track(eventName, data = {}) {
      return quizSupport.track(eventName, data, this);
    }

    _commitSuccessfulEmail(email, leadEventId) {
      if (this.qaMode) return;
      // This boundary is reached only after /api/email returns a non-deferred
      // successful 2xx. It confirms email identity/marketing consent only;
      // Customer Privacy independently governs analytics and advertising sinks.
      const customerPrivacy = quizSupport.customerPrivacySnapshot();
      if (quizSupport.browserIdentityAllowed(customerPrivacy)) {
        try {
          window.dispatchEvent(new CustomEvent('anatomy:identify-email', {
            detail: {
              email,
              reason: 'quiz_email_capture',
              extra: {
                anatomy_quiz_session_id: this.sessionId,
                quiz_completed: true,
                quiz_total_questions: this.questionCount || (this.conversationHistory || []).length
              }
            }
          }));
        } catch (err) { /* identity bridge is progressive enhancement */ }
      }
      this._track('Lead', {
        _fbStandard: 'Lead',
        _fbEventId: leadEventId,
        _gaEvent: 'generate_lead',
        content_name: 'Hair Consultation',
        value: 0,
        currency: 'USD'
      });

      try { sessionStorage.setItem('anatomy:quiz-identity-complete', this.sessionId); }
      catch (err) { /* storage is an enhancement */ }
    }

    _storeAndRedirect(result, email) {
      if (this._loadingProgressTimer) {
        clearInterval(this._loadingProgressTimer);
        this._loadingProgressTimer = null;
      }
      this._setAssessmentProgress(1, 'Your profile is ready');
      this._replaceSensitiveCompletionState(result, !!email && !this.qaMode);
      this._goToResults();
    }

    _normalizeResultForStorage(result) {
      const source = result && typeof result === 'object' ? result : this._getFallbackResult();
      const normalized = {};
      const textKeys = [
        'assessment_version', 'damage_severity', 'diagnostic_scope', 'scope_note',
        'damage_profile_headline', 'damage_profile_summary', 'what_is_happening',
        'key_insight', 'why_previous_products_underperformed',
        'how_anatomy_works_for_you', 'purchase_nudge'
      ];
      textKeys.forEach((key) => {
        if (typeof source[key] === 'string') normalized[key] = source[key].slice(0, 4000);
      });
      ['in_scope', 'commerce_allowed', 'assessment_unavailable'].forEach((key) => {
        if (typeof source[key] === 'boolean') normalized[key] = source[key];
      });
      if (Number.isFinite(Number(source.schema_version))) {
        normalized.schema_version = Number(source.schema_version);
      }
      const protocol = source.reconstruction_protocol;
      if (protocol && typeof protocol === 'object') {
        normalized.reconstruction_protocol = {};
        ['entry_point', 'intensive_phase', 'maintenance_phase', 'expected_timeline', 'personalized_note'].forEach((key) => {
          if (typeof protocol[key] === 'string') normalized.reconstruction_protocol[key] = protocol[key].slice(0, 2000);
        });
      }
      if (Array.isArray(source.clinical_proof)) {
        normalized.clinical_proof = source.clinical_proof.slice(0, 4).map((proof) => {
          if (typeof proof === 'string') return proof.slice(0, 1000);
          if (!proof || typeof proof !== 'object') return null;
          return {
            stat: typeof proof.stat === 'string' ? proof.stat.slice(0, 120) : '',
            context: typeof proof.context === 'string' ? proof.context.slice(0, 1000) : ''
          };
        }).filter(Boolean);
      }
      if (source._meta && Number.isFinite(Number(source._meta.total_questions))) {
        normalized._meta = { total_questions: Math.max(0, Math.min(6, Number(source._meta.total_questions))) };
      }
      return normalized;
    }

    _replaceSensitiveCompletionState(result, identitySaved) {
      const normalizedResult = this._normalizeResultForStorage(result);
      const completedAt = new Date().toISOString();
      const summary = {
        version: 2,
        sessionId: this.sessionId,
        completedAt,
        identitySaved: identitySaved === true,
        primaryConcern: (() => {
          const firstTurn = (this.conversationHistory && this.conversationHistory[0]) || {};
          if (firstTurn.input_type === 'freeform') return 'freeform';
          return String(firstTurn.answer || 'unspecified').replace(/\s+/g, ' ').trim().slice(0, 160) || 'unspecified';
        })(),
        totalQuestions: Math.max(1, Math.min(6, Number(this.questionCount) || (this.conversationHistory || []).length || 1)),
        damageSeverity: normalizedResult.damage_severity || '',
        diagnosticScope: normalizedResult.diagnostic_scope || '',
        commerceAllowed: normalizedResult.commerce_allowed === true,
        entryPoint: normalizedResult.reconstruction_protocol?.entry_point || ''
      };
      const resultContext = {
        version: 2,
        sessionId: this.sessionId,
        sessionToken: this.sessionToken || '',
        sessionExpiresAt: Number(this.sessionExpiresAt) || 0,
        anatomyVisitorId: this.visitorId(),
        identitySaved: identitySaved === true,
        summary,
        ts: Date.now()
      };

      try {
        [
          'anatomy:quiz-answers',
          'anatomy:quiz-prelim',
          'anatomy:quiz-stream-started',
          'anatomy:quiz-progress-v1',
          'anatomy:quiz-result-context',
          'anatomy:quiz-results',
          'anatomy:quiz-offer',
          'anatomy:saved-result-token'
        ].forEach((key) => sessionStorage.removeItem(key));
        sessionStorage.setItem('anatomy:quiz-results', JSON.stringify(normalizedResult));
        sessionStorage.setItem('anatomy:quiz-result-context', JSON.stringify(resultContext));
        sessionStorage.setItem('anatomy:quiz-completion-v2', JSON.stringify(summary));
        if (identitySaved) sessionStorage.setItem('anatomy:quiz-identity-complete', this.sessionId);
        else sessionStorage.removeItem('anatomy:quiz-identity-complete');
      } catch (err) {
        console.error('Quiz result storage failed:', err);
      }

      // Remove raw consultation text and identity from live JS/DOM state before
      // navigating; the results page receives only the allowlisted assessment.
      this.conversationHistory = [];
      this.profileHistory = [];
      this.profile = null;
      this._lastAdaptiveResponse = null;
      this._lastPreliminary = null;
      this._currentLegacyQuestion = '';
      this._email = '';
      this._leadEventId = '';
      const emailInput = this.querySelector('.quiz-email-input');
      if (emailInput) {
        emailInput.value = '';
        emailInput.readOnly = false;
        emailInput.removeAttribute('aria-invalid');
      }
    }

    _goToResults() {
      quizSupport.goToResults(this);
    }

    _animateLoadingSteps() {
      if (this._loadingProgressTimer) {
        clearInterval(this._loadingProgressTimer);
        this._loadingProgressTimer = null;
      }

      const initial = this._assessmentProgress.value || 0.04;
      this._setAssessmentProgress(initial, this._assessmentProgress.label || 'Analyzing damage vectors');
      if (this._assessmentPrefetchPromise) return;

      // Legacy non-stream assessments expose no server milestones. Keep their
      // percentage bounded below completion until the response actually arrives.
      this._loadingProgressTimer = setInterval(() => {
        if (this.currentStep !== 'assessment-loading') {
          clearInterval(this._loadingProgressTimer);
          this._loadingProgressTimer = null;
          return;
        }
        const current = this._assessmentProgress.value || 0.04;
        const next = Math.min(0.9, current + Math.max(0.01, (0.9 - current) * 0.08));
        const label = next >= 0.82
          ? 'Preparing your results'
          : next >= 0.55
            ? 'Building your reconstruction protocol'
            : next >= 0.24
              ? 'Mapping cortex condition'
              : 'Analyzing damage vectors';
        this._setAssessmentProgress(next, label);
      }, 700);
    }

    /* ── Fallback Result ── */

    _getFallbackResult() {
      return quizSupport.getFallbackResult();
    }

    _retake() {
      this.conversationHistory = [];
      this.questionCount = 0;
      this._lastQuestionComplete = false;
      this._lastAdaptiveResponse = null;
      this._assessmentPromise = null;
      this._assessmentResult = null;
      this._assessmentError = null;
      this._assessmentPrefetchPromise = null;
      this._assessmentPrefetchResult = null;
      this._assessmentPrefetchError = null;
      this._assessmentProgress = { value: 0, label: '' };
      this._assessmentTransitionPending = false;
      if (this._loadingProgressTimer) {
        clearInterval(this._loadingProgressTimer);
        this._loadingProgressTimer = null;
      }
      this._completedFired = false;
      this._leadEventId = '';
      this.sessionId = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2);
      this.sessionToken = '';
      this.sessionExpiresAt = 0;
      quizSupport.ensureSession(this).catch(err => console.error('[Quiz] session reset failed:', err));

      // Adaptive state reset
      if (this.adaptiveMarkup) {
        this.profile = this.useAdaptiveBank ? this._initProfile() : null;
        this.profileHistory = [];
        this._currentNode = this.useAdaptiveBank ? this._anchorNode() : null;
        this._turnLocked = false;
        this._lastPct = 0;
        this._pendingMicroInsight = '';
        this._lastPreliminary = null;
        this.querySelectorAll('.quiz-chip--selected').forEach(c => c.classList.remove('quiz-chip--selected'));
        const ta = this.querySelector('[data-step="adaptive"] [data-freetext]');
        if (ta) ta.value = '';
        try {
          sessionStorage.removeItem('anatomy:quiz-prelim');
          sessionStorage.removeItem('anatomy:quiz-stream-started');
          sessionStorage.removeItem('anatomy:quiz-progress-v1');
        } catch (e) { /* silent */ }
      }

      // Clear anchor selections
      this.querySelectorAll('[data-step="anchor"] .quiz-option').forEach(o => {
        o.classList.remove('quiz-option--selected');
        o.setAttribute('aria-checked', 'false');
      });

      // Clear email
      const emailInput = this.querySelector('.quiz-email-input');
      if (emailInput) emailInput.value = '';
      this._setEmailSubmissionState('idle');
      this._clearPreview();

      try {
        sessionStorage.removeItem('anatomy:quiz-answers');
        sessionStorage.removeItem('anatomy:quiz-results');
        sessionStorage.removeItem('anatomy:quiz-result-context');
        sessionStorage.removeItem('anatomy:quiz-completion-v2');
        sessionStorage.removeItem('anatomy:quiz-identity-complete');
        sessionStorage.removeItem('anatomy:quiz-offer');
        sessionStorage.removeItem('anatomy:saved-result-token');
        sessionStorage.removeItem('anatomy:quiz-aview');   // re-arm assessment-viewed once-guard for the retake
        sessionStorage.removeItem('anatomy:quiz-progress-v1');
      } catch (e) { /* silent */ }

      this._showStep('anchor', 'back');
    }

    /* ── State Persistence ── */

    _restoreState() {
      // Never redirect in the theme editor
      if (typeof Shopify !== 'undefined' && Shopify.designMode) return;

      try {
        const results = sessionStorage.getItem('anatomy:quiz-results');
        if (results) {
          // Results exist -- redirect to results page
          this._goToResults();
          return;
        }
      } catch (e) { /* silent */ }

      if (!this.useAdaptiveBank) return;
      try {
        const raw = sessionStorage.getItem('anatomy:quiz-progress-v1');
        if (!raw) return;
        const saved = JSON.parse(raw);
        const createdAt = Number(saved.ts || 0);
        const sessionExpiresAt = Number(saved.sessionExpiresAt || 0);
        const oneHourExpiresAt = createdAt + 60 * 60 * 1000;
        const expiresAt = Number.isFinite(sessionExpiresAt) && sessionExpiresAt > 0
          ? Math.min(sessionExpiresAt, oneHourExpiresAt)
          : oneHourExpiresAt;
        if (
          saved.version !== 1
          || !Number.isFinite(createdAt)
          || createdAt <= 0
          || createdAt > Date.now() + 60000
          || Date.now() >= expiresAt
          || !saved.profile
        ) {
          this._clearProgress();
          this.conversationHistory = [];
          this.profileHistory = [];
          this._lastPreliminary = null;
          this.profile = this._initProfile();
          return;
        }

        this.sessionId = saved.sessionId || this.sessionId;
        if (saved.sessionToken && Number(saved.sessionExpiresAt) > Date.now() + 30000) {
          this.sessionToken = saved.sessionToken;
          this.sessionExpiresAt = Number(saved.sessionExpiresAt);
        }
        this.profile = saved.profile;
        this.conversationHistory = Array.isArray(saved.conversationHistory) ? saved.conversationHistory : [];
        this.profileHistory = Array.isArray(saved.profileHistory) ? saved.profileHistory : [];
        this.questionCount = (this.profile.asked_ids || []).length;
        this._lastPreliminary = saved.preliminary || null;

        if (saved.phase === 'email') {
          this._completedFired = true;
          const prelim = this._lastPreliminary || this._buildPreliminary(this.profile);
          this._lastPreliminary = prelim;
          this._renderPreliminary(prelim);
          this._showStep('email');
          this._startAssessmentPrefetch();
          return;
        }

        const node = this._resolveNode(saved.currentNodeId);
        if (node && node.id !== 'anchor') {
          this._renderBankQuestion(node);
          this._showStep('adaptive');
        }
      } catch (e) {
        this._clearProgress();
      }
    }

    _persistProgress(phase = 'adaptive') {
      if (!this.useAdaptiveBank || (typeof Shopify !== 'undefined' && Shopify.designMode)) return;
      try {
        sessionStorage.setItem('anatomy:quiz-progress-v1', JSON.stringify({
          version: 1,
          ts: Date.now(),
          phase,
          sessionId: this.sessionId,
          sessionToken: this.sessionToken,
          sessionExpiresAt: this.sessionExpiresAt,
          currentNodeId: this._currentNode && this._currentNode.id,
          profile: this.profile,
          conversationHistory: this.conversationHistory,
          profileHistory: this.profileHistory,
          preliminary: this._lastPreliminary
        }));
      } catch (e) { /* storage is an enhancement, never a blocker */ }
    }

    _clearProgress() {
      try { sessionStorage.removeItem('anatomy:quiz-progress-v1'); } catch (e) { /* silent */ }
    }

    /* ═══════════════════════════════════════════════════════════
       ADAPTIVE FREE-TEXT MODE  (behind config.adaptiveBank)
       Chip tap = instant local route (no LLM). Typing = one /api/route
       call, then merge + score + select client-side. Falls back to the
       live /api/question flow on ANY error.
       ═══════════════════════════════════════════════════════════ */

    /* ── Profile lifecycle ── */

    _initProfile() {
      if (this.bank && typeof this.bank.initProfile === 'function') {
        try { return this.bank.initProfile(this.sessionId); } catch (e) { /* fall through */ }
      }
      return this._localInitProfile();
    }

    _localInitProfile() {
      const now = new Date().toISOString();
      return {
        schema_version: 2,
        session_id: this.sessionId,
        asked_ids: [],
        turn_count: 0,
        frustration_theme: null,
        goal: null,
        signals: {
          chemical: { process: 'unknown', overlap: 'unknown', frequency: 'unknown', recency: 'unknown', stacking: 'unknown', load: 'unknown' },
          thermal: { frequency: 'unknown', protectant: 'unknown', temp: 'unknown', load: 'unknown' },
          mechanical: { friction: 'unknown', context: 'unknown', load: 'unknown' },
          hydration: { porosity: 'unknown', balance: 'unknown', product_response: 'unknown', coating_dependent: false },
          bond: { elasticity: 'unsure', integrity: 'unknown' },
          severity: { cuticle: 'unknown', breakage: 'unknown' },
          loss_type: 'unknown'
        },
        severity_score: 0,
        severity: 'mild',
        dominant_mechanism: null,
        secondary_mechanisms: [],
        covered_dimensions: [],
        confidence: 0,
        flags: { vague_last: false, contradiction: false, contradiction_note: null, off_topic_count: 0, degraded: false, shedding_suspected: false },
        narrative: { bridge_text: '', micro_insight: '' },
        created_at: now,
        updated_at: now
      };
    }

    _cloneProfile(p) {
      try { return structuredClone(p); } catch (e) { return JSON.parse(JSON.stringify(p)); }
    }

    /* ── Node resolution (structure from bank, copy from QuizConfig.bank) ── */

    _anchorNode() {
      const resolved = this._resolveNode('anchor');
      if (resolved) return resolved;
      return {
        id: 'anchor', dimension: 'context', allow_free_text: true,
        prompt: this.strings.anchorQuestion || '', context: '', chips: [], _base: null
      };
    }

    _resolveNode(id) {
      const base = (this.bank && this.bank.NODES && this.bank.NODES[id]) || null;
      const copy = (this.config.bank && this.config.bank[id]) || null;
      if (!base && !copy) return null;
      const baseChips = (base && base.chips) || [];
      const copyChips = (copy && copy.chips) || [];
      // Prefer the bank's structural chips (they carry signal/severity AND an
      // authoritative English label); fall back to copy-only chips when the
      // bank is absent for this node.
      const source = baseChips.length ? baseChips : copyChips;
      const chips = source.map((c) => {
        // Localized copy overrides only on an exact value-match; otherwise the
        // bank's own value-correct label wins (never an index guess).
        const byVal = copyChips.find(x => x && x.value === c.value);
        const label = (byVal && byVal.label) || c.label || c.value;
        return {
          value: c.value,
          label: label,
          signal: c.signal || (byVal && byVal.signal) || null,
          severity: (typeof c.severity === 'number') ? c.severity : ((byVal && byVal.severity) || 0)
        };
      });
      const allowFreeText = base
        ? (base.allowFreeText !== false && base.allow_free_text !== false)
        : true;
      return {
        id: id,
        dimension: (base && base.dimension) || (copy && copy.dimension) || '',
        allow_free_text: allowFreeText,
        prompt: (copy && copy.prompt) || (base && base.prompt) || '',
        context: (copy && copy.context) || (base && base.context) || '',
        chips: chips,
        _base: base
      };
    }

    _lookupChip(node, value) {
      const fromNode = node && node.chips && node.chips.find(c => c.value === value);
      if (fromNode && fromNode.signal) return fromNode;
      if (node && node.id === 'anchor' && ANCHOR_FALLBACK_SIGNAL[value]) {
        return { value: value, signal: ANCHOR_FALLBACK_SIGNAL[value].signal, severity: ANCHOR_FALLBACK_SIGNAL[value].severity };
      }
      return fromNode || { value: value, signal: {}, severity: 0 };
    }

    /* ── Composer input ── */

    _activeFreeTextEl() {
      if (this.adaptiveMarkup) return this.querySelector('.quiz-step:not([hidden]) [data-freetext]');
      return this.querySelector('.quiz-step:not([hidden]) [data-transcript]');
    }

    _onComposerInput(ta) {
      const composer = ta.closest('.quiz-composer');
      const send = composer && composer.querySelector('[data-freetext-submit]');
      if (send) send.disabled = !ta.value.trim();
      this._autosize(ta);
    }

    _onComposerKeydown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        if (e.isComposing) return;   // don't submit mid-IME-composition
        e.preventDefault();
        this._onFreeTextSubmit();
      }
    }

    _autosize(ta) {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 160) + 'px';
    }

    /* ── Chip tap (instant, no LLM) ── */

    _onChip(chipEl) {
      if (this._legacyChipMode) return this._onLegacyChip(chipEl);
      if (!this.useAdaptiveBank) return;
      if (this._turnLocked) return;
      this._turnLocked = true;

      const node = this._currentNode;
      if (!node) { this._turnLocked = false; return; }
      const snapshot = this._cloneProfile(this.profile);
      this._paintChipSelected(chipEl);

      const chip = chipEl._chip || this._lookupChip(node, chipEl.dataset.value);
      const signal = (chip && chip.signal) || {};
      const severity = (chip && chip.severity) || 0;
      const label = (chipEl.textContent || chipEl.dataset.value || '').trim();
      const firstTurn = this.conversationHistory.length === 0;

      try {
        // Canonical path: applyChip = mergeSignal + record asked + scoreProfile.
        // scoreProfile recomputes severity_score/severity/confidence from the
        // signals (the single authority), so no manual accumulation here.
        if (typeof this.bank.applyChip === 'function') {
          this.bank.applyChip(this.profile, { value: chipEl.dataset.value, signal: signal, severity: severity }, node.id);
        } else {
          this.bank.mergeSignal(this.profile, signal);
          if (!this.profile.asked_ids.includes(node.id)) this.profile.asked_ids.push(node.id);
          this.bank.scoreProfile(this.profile);
        }
      } catch (e) {
        console.error('[Quiz] chip scoring failed — degrading to live question flow:', e);
        this._turnLocked = false;
        return this._fallbackToLegacyChips();
      }

      if (firstTurn) {
        this._track('QuizStarted', { content_name: 'Hair Consultation', primary_concern: chipEl.dataset.value });
      }
      this.conversationHistory.push({ question: node.prompt || '', answer: chipEl.dataset.value, answer_label: label, input_type: 'chip' });
      this.questionCount = this.profile.asked_ids.length;
      this._track('QuizProgress', { content_name: 'Hair Consultation', question_number: this.questionCount, answer: label, step_index: this.questionCount, question_id: node.id, dimension: node.dimension, input_mode: 'chip' });

      // Fire-and-forget KV/analytics — NEVER on the critical path.
      this._beaconRoute('chip', chipEl.dataset.value, signal);

      // Let the --selected state paint, then route locally.
      requestAnimationFrame(() => setTimeout(() => this._commitTurnAndAdvance(node, snapshot), 120));
    }

    _paintChipSelected(chipEl) {
      const group = chipEl.closest('[data-quiz-chips]');
      if (group) group.querySelectorAll('.quiz-chip').forEach(c => c.classList.remove('quiz-chip--selected'));
      chipEl.classList.add('quiz-chip--selected');
    }

    /* ── Free-text submit (one /api/route call) ── */

    async _onFreeTextSubmit() {
      if (this._legacyChipMode) return this._onFreeTextSubmitLegacy();
      if (!this.useAdaptiveBank) return;
      if (this._turnLocked) return;

      const ta = this._activeFreeTextEl();
      const text = (ta && ta.value || '').trim();
      if (!text) return;
      this._turnLocked = true;

      const mode = this.isRecording ? 'voice' : 'text';   // capture BEFORE stop
      this._stopRecording();
      const node = this._currentNode;
      if (!node) { this._turnLocked = false; return; }
      const snapshot = this._cloneProfile(this.profile);
      const firstTurn = this.conversationHistory.length === 0;

      if (firstTurn) {
        this._track('QuizStarted', { content_name: 'Hair Consultation', primary_concern: 'freeform' });
      }
      this.conversationHistory.push({ question: node.prompt || '', answer: 'freeform', answer_label: text, input_type: mode });
      this._track('QuizProgress', { content_name: 'Hair Consultation', question_number: (this.profile.asked_ids.length + 1), answer: text, input_type: 'freeform', step_index: (this.profile.asked_ids.length + 1), question_id: node.id, dimension: node.dimension, input_mode: mode });
      if (ta) { ta.value = ''; this._autosize(ta); }

      this._pendingBridgeText = this.config.composerReading || this.strings.composerReading || null;
      this._showBridgeText();
      this._showStep('question-loading', 'forward');

      let data = null;
      try {
        data = await this._route(node, mode, text);
      } catch (err) {
        if (err && err.name === 'AbortError') { this._turnLocked = false; return; }
        console.error('[Quiz] /api/route failed — degrading to live question flow:', err);
        this._turnLocked = false;
        return this._fallbackToLegacyChips();
      }

      // Off-topic → do not advance; re-ask the same node once.
      if (data && data.flags && data.flags.off_topic) {
        this.conversationHistory.pop();
        this._pendingMicroInsight = '';
        const isAnchor = node.id === 'anchor';
        const step = isAnchor ? 'anchor' : 'adaptive';
        const loadingStep = this.querySelector('[data-step="question-loading"]');
        if (loadingStep) { loadingStep.hidden = true; loadingStep.classList.remove('quiz-step--exit-left', 'quiz-step--exit-right'); }
        if (!isAnchor) this._renderBankQuestion(node);   // anchor markup is static — nothing to re-render
        const hint = this.querySelector(`[data-step="${step}"] [data-composer-hint]`);
        if (hint) hint.textContent = (data.bridge_text || this.config.offTopicRedirect || this.strings.offTopicRedirect || hint.textContent);
        this._showStep(step, 'forward');
        this._turnLocked = false;
        return;
      }

      try {
        if (data && data.patches) this.bank.mergeSignal(this.profile, data.patches);
        if (data && data.flags && data.flags.shedding_suspected) {
          this.profile.flags = this.profile.flags || {};
          this.profile.flags.shedding_suspected = true;
        }
        if (data && data.next_question_hint) this.profile.next_hint = data.next_question_hint;
        if (!this.profile.asked_ids.includes(node.id)) this.profile.asked_ids.push(node.id);
        this.profile.turn_count = (this.profile.turn_count || 0) + 1;
        this.bank.scoreProfile(this.profile);
        this._pendingBridgeText = (data && data.bridge_text) || this._getDefaultBridgeText();
        this._pendingMicroInsight = '';   // insight now arrives from the deferred /api/insight call
      } catch (e) {
        console.error('[Quiz] route apply failed — degrading to live question flow:', e);
        this._turnLocked = false;
        return this._fallbackToLegacyChips();
      }

      this.questionCount = this.profile.asked_ids.length;
      const loadingStep = this.querySelector('[data-step="question-loading"]');
      if (loadingStep) { loadingStep.hidden = true; loadingStep.classList.remove('quiz-step--exit-left', 'quiz-step--exit-right'); }
      this._commitTurnAndAdvance(node, snapshot);

      // Fill in the personalized reflection a beat later — this NEVER blocks the next question.
      const insightSeq = (this._insightSeq = (this._insightSeq || 0) + 1);
      this._fetchInsight(node, text).then((micro) => this._applyDeferredInsight(insightSeq, micro));
    }

    _route(node, mode, text) {
      if (!this.config.workerUrl) return Promise.reject(new Error('worker URL not configured'));
      this.abortController?.abort();
      this.abortController = new AbortController();
      const timeout = setTimeout(() => this.abortController.abort(), 25000);
      return quizSupport.workerFetch(this, '/api/route', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: this.sessionId,
          anatomy_visitor_id: this.visitorId(),
          customer_privacy: quizSupport.customerPrivacySnapshot(),
          posthog_session_id: quizSupport.posthogSessionId(),
          turn: { question_id: node.id, dimension: node.dimension },
          answer: { mode: mode, chip_id: null, signal: null, text: text },
          profile: this.profile,
          bank_manifest: (this.bank && this.bank.MANIFEST) || []
        }),
        signal: this.abortController.signal
      })
        .then(r => { clearTimeout(timeout); if (!r.ok) throw new Error(`route ${r.status}`); return r.json(); })
        .catch(err => { clearTimeout(timeout); throw err; });
    }

    _beaconRoute(mode, chipId, signal) {
      if (!this.config.workerUrl) return;
      try {
        const body = JSON.stringify({
          session_id: this.sessionId,
          anatomy_visitor_id: this.visitorId(),
          customer_privacy: quizSupport.customerPrivacySnapshot(),
          posthog_session_id: quizSupport.posthogSessionId(),
          turn: { question_id: this._currentNode && this._currentNode.id, dimension: this._currentNode && this._currentNode.dimension },
          answer: { mode: 'chip', chip_id: chipId, signal: signal, text: '' },
          profile: this.profile
        });
        quizSupport.workerFetch(this, '/api/route', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true
        }).catch(() => {});
      } catch (e) { /* analytics only — never throws into the UI */ }
    }

    /* ── Deferred insight (decoupled from the blocking classify call) ── */

    _fetchInsight(node, text) {
      if (!this.config.workerUrl || !text) return Promise.resolve('');
      return quizSupport.workerFetch(this, '/api/insight', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: this.sessionId,
          anatomy_visitor_id: this.visitorId(),
          customer_privacy: quizSupport.customerPrivacySnapshot(),
          answer_text: text,
          question_text: (node && node.prompt) || ''
        })
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => (d && typeof d.micro_insight === 'string') ? d.micro_insight : '')
        .catch(() => '');
    }

    _applyDeferredInsight(seq, micro) {
      if (!micro || seq !== this._insightSeq) return;   // superseded by a newer turn
      const step = this.querySelector('[data-step="adaptive"]:not([hidden])');
      if (!step) return;                                // no longer on an adaptive question
      const el = step.querySelector('[data-adaptive-insight]');
      if (!el) return;
      el.textContent = micro;
      el.hidden = false;
    }

    /* ── Selector + advance ── */

    _commitTurnAndAdvance(node, snapshot) {
      this.profileHistory.push({ profile: snapshot, nodeId: node.id });
      let result;
      try { result = this.bank.selectNext(this.profile); }
      catch (e) { console.error('[Quiz] selectNext failed — degrading:', e); this._turnLocked = false; return this._fallbackToLegacyChips(); }
      if (!result) { this._turnLocked = false; return this._fallbackToLegacyChips(); }

      if (result.finish || result.done || result.complete) {
        return this._finishAdaptive();
      }
      const nextId = result.id || result.next || result.question_id || result.node;
      const next = nextId ? this._resolveNode(nextId) : null;
      if (!next) {
        // Bank wants a node we have no copy/structure for — finish cleanly
        // rather than dead-end (preliminary is always brand-safe).
        if ((this.profile.asked_ids || []).length >= (this.config.minQuestions || 4)) return this._finishAdaptive();
        this._turnLocked = false;
        return this._fallbackToLegacyChips();
      }
      this._renderBankQuestion(next);
      this._showStep('adaptive', 'forward');
      this._persistProgress('adaptive');
      this._turnLocked = false;
    }

    _renderBankQuestion(node) {
      this._currentNode = node;
      const insightEl = this.querySelector('[data-step="adaptive"] [data-adaptive-insight]');
      const contextEl = this.querySelector('[data-step="adaptive"] [data-adaptive-context]');
      const questionEl = this.querySelector('[data-step="adaptive"] [data-adaptive-question]');
      const chipsEl = this.querySelector('[data-step="adaptive"] [data-quiz-chips]');
      const hintEl = this.querySelector('[data-step="adaptive"] [data-composer-hint]');

      if (insightEl) {
        const micro = this._pendingMicroInsight || '';
        insightEl.textContent = micro;
        insightEl.hidden = !micro;
        this._pendingMicroInsight = '';
      }
      if (contextEl) { contextEl.textContent = node.context || ''; contextEl.hidden = !node.context; }
      if (questionEl) questionEl.textContent = node.prompt || '';
      if (hintEl) hintEl.textContent = this.config.composerHint || this.strings.composerHint || hintEl.textContent;

      if (chipsEl) {
        chipsEl.innerHTML = '';
        (node.chips || []).forEach((chip) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'quiz-chip';
          btn.dataset.value = chip.value;
          btn.textContent = chip.label;
          btn._chip = chip;
          chipsEl.appendChild(btn);
        });
      }

      const ta = this.querySelector('[data-step="adaptive"] [data-freetext]');
      if (ta) { ta.value = ''; this._autosize(ta); }
      const send = this.querySelector('[data-step="adaptive"] [data-freetext-submit]');
      if (send) send.disabled = true;

      this._focusQuestion('adaptive');
      this._updateProgressAdaptive();
    }

    _focusQuestion(step) {
      const q = this.querySelector(`[data-step="${step}"] .quiz-question`);
      if (q) { q.setAttribute('tabindex', '-1'); try { q.focus({ preventScroll: false }); } catch (e) {} }
      this._announce(this._progressAnnouncement());
    }

    _announce(msg) {
      const region = this.querySelector('.quiz-step:not([hidden]) [data-quiz-status]') || this.querySelector('[data-quiz-status]');
      if (region) region.textContent = msg || '';
    }

    /* ── Adaptive progress ── */

    _updateProgressAdaptive() {
      const conf = (this.profile && typeof this.profile.confidence === 'number') ? this.profile.confidence : 0;
      let pct = Math.min(8 + conf * 82, 92);
      pct = Math.max(this._lastPct || 0, pct);
      this._lastPct = pct;
      if (this.progressBar) this.progressBar.style.setProperty('--progress', pct + '%');
      if (this.progressEl) {
        this.progressEl.setAttribute('aria-valuemin', '0');
        this.progressEl.setAttribute('aria-valuemax', '6');
        const n = (this.profile && this.profile.asked_ids) ? this.profile.asked_ids.length : 0;
        this.progressEl.setAttribute('aria-valuenow', String(Math.min(n, 6)));
      }
      if (this.phaseEl) {
        const label = this._getPhaseLabel();
        this.phaseEl.textContent = label;
        this.phaseEl.style.opacity = label ? '1' : '0';
      }
    }

    _estimatedTotal() {
      if (this.bank && typeof this.bank.estimatedTotal === 'function') {
        try { const e = this.bank.estimatedTotal(this.profile); if (typeof e === 'number') return e; } catch (err) { /* fall through */ }
      }
      const min = this.config.minQuestions || 4;
      const max = this.config.maxQuestions || 6;
      const conf = (this.profile && this.profile.confidence) || 0;
      const n = (this.profile && this.profile.asked_ids) ? this.profile.asked_ids.length : 0;
      const est = Math.round(min + (max - min) * (1 - conf));
      return Math.max(n, Math.min(max, est));
    }

    _progressAnnouncement() {
      const n = (this.profile && this.profile.asked_ids) ? this.profile.asked_ids.length : 0;
      const est = this._estimatedTotal();
      const remaining = Math.max(0, Math.min(est, 6) - n);
      if (remaining <= 1) return this.config.progressAlmostThere || this.strings.progressAlmostThere || 'Almost there';
      const tmpl = this.config.progressAboutToGo || 'Question {n} · about {rest} to go';
      return tmpl.replace('{n}', String(n + 1)).replace('{rest}', String(remaining));
    }

    /* ── Finish → instant preliminary ── */

    _finishAdaptive() {
      let prelim;
      try { prelim = this._buildPreliminary(this.profile); }
      catch (e) { console.error('[Quiz] buildPreliminary failed:', e); prelim = this._buildPreliminaryFallback(this.profile); }
      this._lastPreliminary = prelim;
      if (!this._completedFired) {
        this._completedFired = true;
        this._track('QuizCompleted', {
          total_questions: (this.profile && this.profile.asked_ids ? this.profile.asked_ids.length : this.questionCount) || 0,
          primary_concern: (this.conversationHistory[0] && this.conversationHistory[0].answer) || '',
          severity: (prelim && prelim.damage_severity) || (this.profile && this.profile.severity) || '',
          dominant_mechanism: (this.profile && this.profile.dominant_mechanism) || '',
          confidence: (this.profile && this.profile.confidence) || 0,
          entry_point: (prelim && prelim.reconstruction_protocol && prelim.reconstruction_protocol.entry_point) || '',
          completion_path: this._legacyChipMode ? 'legacy' : 'adaptive',
          had_freetext: (this.conversationHistory || []).some(t => t && t.input_type === 'freeform')
        });
      }
      this._renderPreliminary(prelim);
      this._showStep('email', 'forward');
      this._persistProgress('email');
      this._startAssessmentPrefetch();
      this._turnLocked = false;
    }

    _buildPreliminary(profile) {
      if (this.bank && typeof this.bank.buildPreliminary === 'function') {
        try {
          const p = this.bank.buildPreliminary(profile);
          if (p && (p.damage_severity || p.damage_profile_headline)) return p;
        } catch (e) { console.error('[Quiz] bank.buildPreliminary failed:', e); }
      }
      return this._buildPreliminaryFallback(profile);
    }

    /* Qualitative only — NEVER a number or "%" here. Lab references
       belong to the streamed gpt-5-mini stage where they are framed correctly. */
    _buildPreliminaryFallback(profile) {
      const p = profile || {};
      const severity = p.severity || 'moderate';
      const mech = p.dominant_mechanism || 'chemical';
      const theme = p.frustration_theme || '';
      const isPrevention = theme === 'prevention' || (severity === 'mild' && (p.severity_score || 0) === 0);

      const mechLabel = {
        chemical: 'chemical processing',
        thermal: 'heat styling',
        mechanical: 'daily mechanical stress',
        hydration: 'a moisture and porosity imbalance'
      }[mech] || 'cumulative stress';

      if (isPrevention) {
        return {
          damage_severity: 'mild',
          commerce_allowed: false,
          damage_profile_headline: "Your hair is in good shape — let's keep it that way",
          damage_profile_summary: 'Your answers point to a strong starting condition. The priority is preserving that baseline without turning prevention into a damage label.',
          key_insight: 'Natural texture and a prevention goal are not evidence of structural damage.',
          reconstruction_protocol: { entry_point: 'none', expected_timeline: 'Reassess only if the fibre changes.' }
        };
      }

      const headlineBySeverity = {
        mild: 'Early structural wear — caught at the right time',
        moderate: 'Your cortex is showing structural fatigue',
        moderate_severe: 'Significant bond loss inside the fibre',
        severe: 'Advanced structural damage through the cortex'
      };
      const summaryBySeverity = {
        mild: 'The early signatures of ' + mechLabel + ' are showing at the surface, with the internal bond network still largely intact.',
        moderate: 'Repeated ' + mechLabel + ' has begun breaking down the bond network inside your cortex — that is what you feel as weakness and roughness.',
        moderate_severe: 'Your cortex has lost a meaningful share of its bond network to ' + mechLabel + ', so strands stretch, snap, or lose their spring.',
        severe: 'Extensive ' + mechLabel + ' has disrupted the bonds that hold your fibre together, leaving strands fragile and unable to hold their shape.'
      };
      const entryBySeverity = { mild: 'targeted_start', moderate: 'targeted_start', moderate_severe: 'full_system', severe: 'full_system' };

      return {
        damage_severity: severity,
        damage_profile_headline: headlineBySeverity[severity] || headlineBySeverity.moderate,
        damage_profile_summary: summaryBySeverity[severity] || summaryBySeverity.moderate,
        key_insight: 'Your damage pattern is led by ' + mechLabel + ' — exactly what targeted reconstruction is built to rebuild from the inside.',
        reconstruction_protocol: { entry_point: entryBySeverity[severity] || 'full_system', expected_timeline: '' }
      };
    }

    _renderPreliminary(prelim) {
      prelim = prelim || {};
      const severityEl = this.querySelector('[data-preview-severity]');
      const timelineEl = this.querySelector('[data-preview-timeline]');
      const headlineEl = this.querySelector('[data-preview-headline]');
      const summaryEl = this.querySelector('[data-preview-summary]');
      const insightEl = this.querySelector('[data-preview-insight]');
      const entryPointEl = this.querySelector('[data-preview-entry-point]');
      const proto = prelim.reconstruction_protocol || {};

      if (severityEl) severityEl.textContent = this._formatSeverity(prelim.damage_severity);
      if (timelineEl) {
        const t = proto.expected_timeline || '';
        timelineEl.textContent = t;
        timelineEl.hidden = !t;
      }
      if (headlineEl) headlineEl.textContent = prelim.damage_profile_headline || '';
      if (summaryEl) summaryEl.textContent = prelim.damage_profile_summary || '';
      if (insightEl) insightEl.textContent = prelim.key_insight || prelim.what_is_happening || '';
      if (entryPointEl) entryPointEl.textContent = this._formatProtocolEntry(proto.entry_point);
    }

    /* ── Email handoff → results page stream ── */

    async _completeAdaptiveHandoff(email, leadEventId = '') {
      if (!email) {
        this._showStep('assessment-loading');
        this._animateLoadingSteps();
      }

      const prefetch = this._startAssessmentPrefetch();
      const result = this._assessmentPrefetchResult || (prefetch ? await prefetch : null);
      if (!this._isCompleteAssessmentProfile(result)) {
        if (email) {
          throw new Error('We could not complete your assessment yet. Please try again.');
        }
        this._handoffAdaptive();
        return;
      }

      if (email) {
        await this._sendEmail(email, leadEventId);
        this._commitSuccessfulEmail(email, leadEventId);
        this._setEmailSubmissionState('success', 'Saved. Opening your results...');
        this._showStep('assessment-loading');
        this._animateLoadingSteps();
      }
      this._setAssessmentProgress(1, 'Your profile is ready');
      await new Promise((resolve) => setTimeout(resolve, 180));
      this._storeAndRedirect(result, email);
    }

    _handoffAdaptive() {
      const prelim = this._lastPreliminary || this._buildPreliminary(this.profile);
      try {
        sessionStorage.setItem('anatomy:quiz-prelim', JSON.stringify({
          runningProfile: this.profile,
          preliminary: prelim,
          conversationHistory: this.conversationHistory,
          products: this.products || [],
          sessionId: this.sessionId,
          sessionToken: this.sessionToken,
          sessionExpiresAt: this.sessionExpiresAt,
          anatomyVisitorId: this.visitorId(),
          quizMode: this._legacyChipMode ? 'legacy_chip' : 'adaptive_bank',
          totalQuestions: (this.profile && Array.isArray(this.profile.asked_ids))
            ? this.profile.asked_ids.length
            : (Number(this.questionCount) || this.conversationHistory.length || 0),
          streamAssessment: !!this.config.streamAssessment,
          workerUrl: this.config.workerUrl || '',
          ts: Date.now()
        }));
        sessionStorage.removeItem('anatomy:quiz-results');     // force a fresh stream
        sessionStorage.removeItem('anatomy:quiz-result-context');
        sessionStorage.removeItem('anatomy:quiz-completion-v2');
        sessionStorage.removeItem('anatomy:quiz-identity-complete');
        sessionStorage.removeItem('anatomy:quiz-stream-started');
        sessionStorage.removeItem('anatomy:quiz-progress-v1');
      } catch (err) { console.error('[Quiz] prelim handoff store failed:', err); }

      if (typeof Shopify !== 'undefined' && Shopify.designMode) return;  // never redirect in editor
      this._goToResults();
    }

    /* ── Back ── */

    _goBackAdaptive() {
      if (!this.useAdaptiveBank || this._legacyChipMode) return this._goBackChips();

      const prev = this.profileHistory.pop();
      this._lastPct = 0;   // allow the bar to ease back down
      if (this.conversationHistory.length) this.conversationHistory.pop();

      if (!prev || prev.nodeId === 'anchor') {
        this.profile = (prev && prev.profile) ? prev.profile : this._initProfile();
        this.conversationHistory = [];
        this._currentNode = this._anchorNode();
        this.querySelectorAll('[data-step="anchor"] .quiz-chip--selected').forEach(c => c.classList.remove('quiz-chip--selected'));
        this._showStep('anchor', 'back');
        this._clearProgress();
        return;
      }

      this.profile = prev.profile;
      this.questionCount = (this.profile.asked_ids || []).length;
      const node = this._resolveNode(prev.nodeId) || this._anchorNode();
      this._pendingMicroInsight = '';
      this._renderBankQuestion(node);
      this._showStep('adaptive', 'back');
      this._persistProgress('adaptive');
    }

    /* ═══════════════════════════════════════════════════════════
       LEGACY-CHIP FALLBACK — adaptive markup present but no bank (or
       the bank errored mid-flow). Drives the chips + composer with the
       live /api/question flow so the quiz never dead-ends.
       ═══════════════════════════════════════════════════════════ */

    _fallbackToLegacyChips() {
      this.useAdaptiveBank = false;
      this._legacyChipMode = true;
      this.questionCount = this.conversationHistory.length;
      this._lastQuestionComplete = false;
      this._prefetchedQuestion = this._startQuestionFetch();
      this._advanceLegacyChips('forward');
    }

    _onLegacyChip(chipEl) {
      if (this._turnLocked) return;
      this._turnLocked = true;
      this._paintChipSelected(chipEl);
      const value = chipEl.dataset.value;
      const label = (chipEl.textContent || value || '').trim();

      if (this.questionCount === 0) {
        this.conversationHistory = [{ question: this.strings.anchorQuestion || '', answer: value, answer_label: label, input_type: 'option' }];
        this.questionCount = 1;
        this._track('QuizStarted', { content_name: 'Hair Consultation', primary_concern: value });
        this._track('QuizProgress', { content_name: 'Hair Consultation', question_number: 1, answer: label });
        this._pendingBridgeText = this._getDefaultBridgeText();
        this._prefetchedQuestion = this._startQuestionFetch();
        requestAnimationFrame(() => setTimeout(() => this._advanceLegacyChips('forward'), 120));
        return;
      }

      this.conversationHistory.push({ question: this._currentLegacyQuestion || '', answer: value, answer_label: label, input_type: 'option' });
      this.questionCount++;
      this._track('QuizProgress', { content_name: 'Hair Consultation', question_number: this.questionCount, answer: label });

      if (this._lastQuestionComplete || this.questionCount >= 6) {
        this._fireAssessmentEarly();
        requestAnimationFrame(() => setTimeout(() => { this._showAssessmentPreview('forward'); this._turnLocked = false; }, 120));
      } else {
        this._prefetchedQuestion = this._startQuestionFetch();
        requestAnimationFrame(() => setTimeout(() => this._advanceLegacyChips('forward'), 120));
      }
    }

    _onFreeTextSubmitLegacy() {
      if (this._turnLocked) return;
      const ta = this._activeFreeTextEl();
      const text = (ta && ta.value || '').trim();
      if (!text) return;
      this._turnLocked = true;
      const mode = this.isRecording ? 'voice' : 'text';
      this._stopRecording();
      if (ta) { ta.value = ''; this._autosize(ta); }

      if (this.questionCount === 0) {
        this.conversationHistory = [{ question: this.strings.anchorQuestion || '', answer: 'freeform', answer_label: text, input_type: mode }];
        this.questionCount = 1;
        this._track('QuizStarted', { content_name: 'Hair Consultation', primary_concern: 'freeform' });
        this._track('QuizProgress', { content_name: 'Hair Consultation', question_number: 1, answer: text, input_type: 'freeform' });
        this._pendingBridgeText = this._getDefaultBridgeText();
      } else {
        this.conversationHistory.push({ question: this._currentLegacyQuestion || '', answer: 'freeform', answer_label: text, input_type: mode });
        this.questionCount++;
        this._track('QuizProgress', { content_name: 'Hair Consultation', question_number: this.questionCount, answer: text, input_type: 'freeform' });
      }

      if (this._lastQuestionComplete || this.questionCount >= 6) {
        this._fireAssessmentEarly();
        requestAnimationFrame(() => setTimeout(() => { this._showAssessmentPreview('forward'); this._turnLocked = false; }, 120));
      } else {
        this._prefetchedQuestion = this._startQuestionFetch();
        requestAnimationFrame(() => setTimeout(() => this._advanceLegacyChips('forward'), 120));
      }
    }

    async _advanceLegacyChips(direction) {
      this._showBridgeText();
      this._showStep('question-loading', direction);
      try {
        const data = await (this._prefetchedQuestion || this._startQuestionFetch());
        this._prefetchedQuestion = null;
        if (!data) { this._renderFallbackChips(); this._showStep('adaptive', direction); this._turnLocked = false; return; }
        this._lastQuestionComplete = data.complete || false;
        const loadingStep = this.querySelector('[data-step="question-loading"]');
        if (loadingStep) { loadingStep.hidden = true; loadingStep.classList.remove('quiz-step--exit-left', 'quiz-step--exit-right'); }
        if (data.complete) {
          this._fireAssessmentEarly();
          this._showAssessmentPreview(direction);
        } else {
          this._renderLegacyChips(data);
          this._showStep('adaptive', direction);
        }
      } catch (err) {
        this._prefetchedQuestion = null;
        if (err && err.name === 'AbortError') { this._turnLocked = false; return; }
        console.error('[Quiz] legacy-chip fetch failed:', err);
        if (this.questionCount >= 3) { this._fireAssessmentEarly(); this._showAssessmentPreview(direction); }
        else { this._renderFallbackChips(); this._showStep('adaptive', direction); }
      }
      this._turnLocked = false;
    }

    _renderLegacyChips(data) {
      this._currentLegacyQuestion = data.question_text || '';
      const bridge = data.bridge_text || '';
      const isGeneric = !bridge || /\b(loading|please wait|one moment|hang tight|stand by)\b/i.test(bridge) || /\bnext (question|one)\b/i.test(bridge);
      this._pendingBridgeText = isGeneric ? this._getDefaultBridgeText() : bridge;

      const insightEl = this.querySelector('[data-step="adaptive"] [data-adaptive-insight]');
      const contextEl = this.querySelector('[data-step="adaptive"] [data-adaptive-context]');
      const questionEl = this.querySelector('[data-step="adaptive"] [data-adaptive-question]');
      const chipsEl = this.querySelector('[data-step="adaptive"] [data-quiz-chips]');

      if (insightEl) { insightEl.textContent = data.micro_insight || ''; insightEl.hidden = !data.micro_insight; }
      if (contextEl) { contextEl.textContent = data.question_context || ''; contextEl.hidden = !data.question_context; }
      if (questionEl) questionEl.textContent = data.question_text || '';
      if (chipsEl) {
        chipsEl.innerHTML = '';
        (data.options || []).forEach((opt) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'quiz-chip';
          btn.dataset.value = opt.value;
          btn.textContent = opt.label;
          chipsEl.appendChild(btn);
        });
      }
      const ta = this.querySelector('[data-step="adaptive"] [data-freetext]');
      if (ta) { ta.value = ''; this._autosize(ta); }
      const send = this.querySelector('[data-step="adaptive"] [data-freetext-submit]');
      if (send) send.disabled = true;
      this._focusQuestion('adaptive');
      this._renderDebug(data);
    }

    _renderFallbackChips() {
      const used = this.conversationHistory.map(h => h.question);
      const fb = (this.fallbackQuestions || []).find(f => !used.includes(f.question_text)) || (this.fallbackQuestions || [])[0];
      if (!fb) { this._fireAssessmentEarly(); this._showAssessmentPreview('forward'); return; }
      this._lastQuestionComplete = true;
      this._renderLegacyChips(fb);
    }

    _goBackChips() {
      if (this.currentStep === 'email') {
        this._showStep('adaptive', 'back');
        return;
      }
      if (this.currentStep === 'adaptive') {
        if (this.conversationHistory.length > 1) { this.conversationHistory.pop(); this.questionCount--; }
        if (this.conversationHistory.length <= 1) {
          this.conversationHistory = [];
          this.questionCount = 0;
          this.querySelectorAll('[data-step="anchor"] .quiz-chip--selected').forEach(c => c.classList.remove('quiz-chip--selected'));
          this._showStep('anchor', 'back');
        } else {
          this._lastQuestionComplete = false;
          this._prefetchedQuestion = null;
          this._advanceLegacyChips('back');
        }
      }
    }

    /* ── Utilities ── */

    _escapeHtml(str) {
      return quizSupport.escapeHtml(str);
    }

    _escapeAttr(str) {
      return quizSupport.escapeAttr(str);
    }
  });
}
